import React from 'react';
import { scaleOrdinal } from '@visx/scale';
import TinyColor from 'tinycolor2';
import { Pane, Flex, Text } from '@leverege/ui-elements';
import { usePieData } from "../hooks/index.js";
import S from './KeyRenderer.css';
import { getAggTitle } from "./Util.js";
export default function KeyRenderer({
  data,
  attribute,
  isAgg,
  sortValues = true,
  sourceObjectType,
  query,
  limit,
  includeNull,
  valueRanges,
  moveKey,
  theme,
  outerRadius,
  width,
  height,
  margin,
  pieLeft,
  hideTitle,
  active,
  keyConfig
}) {
  const keyData = usePieData(data, {
    isAgg,
    query,
    attribute,
    valueRanges,
    limit,
    sortValues,
    includeNull
  });
  const {
    titleVariant,
    labelVariant,
    countVariant,
    animate,
    title
  } = keyConfig;
  const getTitle = isAgg => {
    if (title) return title;
    if (isAgg) {
      return getAggTitle(query, sourceObjectType);
    }
    const typeCheck = attribute.type || attribute.valueType;
    if (!typeCheck.includes('string')) return `${attribute.label || attribute.name}`;
    if ((attribute.label || attribute.name).endsWith('s')) {
      return `${keyData.length} ${attribute.label || attribute.name}`;
    }
    return `${keyData.length} ${attribute.label || attribute.name}s`;
  };
  const getColor = scaleOrdinal({
    domain: data.map((l, i) => i),
    range: [...theme.colors]
  });
  const displayData = keyData.slice(0, 10);
  const normalPaneStyle = {
    position: 'absolute',
    top: hideTitle ? 10 : 0,
    left: pieLeft + outerRadius + width / 20,
    width: width - outerRadius * 2,
    height: height - (margin.top + margin.bottom),
    marginTop: margin.top,
    marginBottom: margin.bottom
  };
  const horizontalPaneStyle = {
    position: 'absolute',
    top: outerRadius * 2 + 10,
    left: 0,
    width: '100%',
    height: height - margin.bottom - margin.top - outerRadius * 2,
    marginBottom: margin.bottom
  };
  const splitData = {
    left: [],
    right: []
  };
  displayData.map((datum, i) => {
    if (i <= 2) splitData.left.push(datum);
    if (i >= 3 && i < 6) splitData.right.push(datum);
    return null;
  });
  const scaledIndex = scaleOrdinal({
    domain: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19],
    range: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
  });
  const computedColor = i => theme.getComputedColor(document.documentElement, scaledIndex(i));
  return /*#__PURE__*/React.createElement(Pane, {
    className: S.keyPane,
    style: moveKey ? horizontalPaneStyle : normalPaneStyle
  }, !moveKey && /*#__PURE__*/React.createElement(Pane, {
    className: S.keyVerticalPane
  }, !hideTitle && /*#__PURE__*/React.createElement(Text, {
    className: S.keyTitle,
    variant: titleVariant || 'subtitle'
  }, getTitle(isAgg)), displayData.map((datum, i) => /*#__PURE__*/React.createElement(Flex, {
    className: S.keyItemFlex
  }, /*#__PURE__*/React.createElement("div", {
    className: animate && active && active.name === datum.name ? S.activeBox : S.box,
    style: {
      backgroundColor: active && active.name === datum.name ? TinyColor(computedColor(i)).darken(10) : getColor(i)
    }
  }), /*#__PURE__*/React.createElement(Text, {
    variant: labelVariant || 'captionBold',
    className: S.keyLabel
  }, datum.name), /*#__PURE__*/React.createElement(Text, {
    variant: countVariant || 'bodySmall',
    className: S.keyValue
  }, datum.value)))), moveKey && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    className: S.keyTitleCenter,
    variant: titleVariant || 'subtitle'
  }, getTitle(isAgg)), /*#__PURE__*/React.createElement(Pane, {
    className: S.keyHorizontalPane,
    style: {
      minWidth: width
    }
  }, /*#__PURE__*/React.createElement(Flex, {
    variant: "colS",
    className: S.splitKeys,
    style: {
      minWidth: (width - (margin.left + margin.right)) / 2.5
    }
  }, splitData.left.map((datum, i) => /*#__PURE__*/React.createElement(Flex, {
    className: S.keyItemFlexSplit
  }, /*#__PURE__*/React.createElement("div", {
    className: animate && active && active.name === datum.name ? S.activeBox : S.box,
    style: {
      backgroundColor: active && active.name === datum.name ? TinyColor(computedColor(i)).darken(10) : getColor(i)
    }
  }), /*#__PURE__*/React.createElement(Text, {
    variant: labelVariant || 'captionBold',
    className: S.keyLabel
  }, datum.name), /*#__PURE__*/React.createElement(Text, {
    variant: countVariant || 'bodySmall',
    className: S.keyValue
  }, datum.value)))), /*#__PURE__*/React.createElement(Flex, {
    variant: "colS",
    className: S.splitKeys,
    style: {
      minWidth: (width - (margin.left + margin.right)) / 2.5
    }
  }, splitData.right.map((datum, i) => /*#__PURE__*/React.createElement(Flex, {
    className: S.keyItemFlexSplit
  }, /*#__PURE__*/React.createElement("div", {
    className: animate && active && active.name === datum.name ? S.activeBox : S.box,
    style: {
      backgroundColor: active && active.name === datum.name ? TinyColor(computedColor(i)).darken(10) : getColor(i)
    }
  }), /*#__PURE__*/React.createElement(Text, {
    variant: labelVariant || 'captionBold',
    className: S.keyLabel
  }, datum.name), /*#__PURE__*/React.createElement(Text, {
    variant: countVariant || 'bodySmall',
    className: S.keyValue
  }, datum.value)))))));
}